import { useState, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Check, 
  Crown,
  Zap,
  Building,
  Star
} from "lucide-react";
import { toast } from "sonner";

const SubscriptionPage = () => {
  const { user } = useAuth();
  const [tiers, setTiers] = useState({});
  const [currentSub, setCurrentSub] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currency, setCurrency] = useState("IDR");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tiersRes, subRes] = await Promise.all([
        axios.get(`${API}/subscription/tiers`),
        axios.get(`${API}/subscription/current`, { withCredentials: true })
      ]);
      setTiers(tiersRes.data);
      setCurrentSub(subRes.data);
    } catch (error) {
      toast.error("Gagal memuat data subscription");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpgrade = async (tierKey) => {
    if (tierKey === "free") {
      toast.info("Anda sudah menggunakan paket Free");
      return;
    }
    
    try {
      await axios.post(
        `${API}/subscription/upgrade`,
        { tier: tierKey },
        { withCredentials: true }
      );
      toast.success(`Berhasil upgrade ke ${tiers[tierKey].name}!`);
      fetchData();
    } catch (error) {
      toast.error("Gagal upgrade subscription");
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return "Rp 0";
    if (currency === "USD") {
      return `$${(price / 15000).toFixed(2)}`;
    }
    return `Rp ${price.toLocaleString("id-ID")}`;
  };

  const tierIcons = {
    free: Zap,
    starter: Star,
    pro: Crown,
    business: Building
  };

  const tierColors = {
    free: "from-gray-400 to-gray-600",
    starter: "from-blue-400 to-blue-600",
    pro: "from-purple-400 to-purple-600",
    business: "from-amber-400 to-amber-600"
  };

  const tierFeatures = {
    free: [
      "30 short links per hari",
      "3 Microsites",
      "QR Code untuk setiap link",
      "Analytics dasar (7 hari)",
      "Powered by Campus Link badge"
    ],
    starter: [
      "Semua fitur Free",
      "100 short links per hari",
      "10 Microsites",
      "Custom alias",
      "Sembunyikan badge",
      "Analytics 30 hari"
    ],
    pro: [
      "Semua fitur Starter",
      "500 short links per hari",
      "50 Microsites",
      "Custom domain untuk Microsite",
      "Custom short domain",
      "QR Code customization lanjutan",
      "Analytics 90 hari"
    ],
    business: [
      "Semua fitur Pro",
      "Unlimited short links",
      "Unlimited Microsites",
      "Custom subdomain",
      "Webhook notification",
      "Analytics 1 tahun",
      "Priority support"
    ]
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const tierOrder = ["free", "starter", "pro", "business"];

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="subscription-page">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: "Plus Jakarta Sans" }}>
            Pilih Paket yang Tepat untuk Anda
          </h1>
          <p className="text-muted-foreground">
            Upgrade kapan saja untuk fitur lebih lengkap
          </p>
          
          {/* Currency Toggle */}
          <div className="flex items-center justify-center gap-2 mt-6">
            <span className="text-sm text-muted-foreground">Currency:</span>
            <div className="flex rounded-full bg-muted p-1">
              <Button
                variant={currency === "IDR" ? "default" : "ghost"}
                size="sm"
                className="rounded-full px-4"
                onClick={() => setCurrency("IDR")}
                data-testid="currency-idr"
              >
                IDR
              </Button>
              <Button
                variant={currency === "USD" ? "default" : "ghost"}
                size="sm"
                className="rounded-full px-4"
                onClick={() => setCurrency("USD")}
                data-testid="currency-usd"
              >
                USD
              </Button>
            </div>
          </div>
        </div>

        {/* Current Usage */}
        {currentSub && (
          <Card className="dashboard-card max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-lg">Penggunaan Saat Ini</CardTitle>
              <CardDescription>
                Anda menggunakan paket <span className="font-medium text-primary">{currentSub.tier_info?.name || "Free"}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-1">Links hari ini</p>
                  <p className="text-2xl font-bold">
                    {currentSub.usage?.links_today || 0}
                    <span className="text-sm font-normal text-muted-foreground">
                      /{currentSub.usage?.links_limit === -1 ? "∞" : currentSub.usage?.links_limit}
                    </span>
                  </p>
                </div>
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground mb-1">Microsites</p>
                  <p className="text-2xl font-bold">
                    {currentSub.usage?.microsites || 0}
                    <span className="text-sm font-normal text-muted-foreground">
                      /{currentSub.usage?.microsites_limit === -1 ? "∞" : currentSub.usage?.microsites_limit}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tierOrder.map((tierKey) => {
            const tier = tiers[tierKey];
            if (!tier) return null;
            
            const Icon = tierIcons[tierKey];
            const isCurrentTier = currentSub?.tier === tierKey;
            const isPro = tierKey === "pro";
            
            return (
              <Card 
                key={tierKey}
                className={`dashboard-card relative ${isPro ? "border-primary shadow-lg shadow-primary/20" : ""}`}
                data-testid={`tier-${tierKey}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">
                      Recommended
                    </Badge>
                  </div>
                )}
                
                {tierKey === "business" && (
                  <div className="absolute top-4 right-4">
                    <Badge variant="outline" className="text-green-500 border-green-500">
                      Discount 30%
                    </Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center bg-gradient-to-br ${tierColors[tierKey]}`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <CardTitle className="text-xl">{tier.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-3xl font-bold">{formatPrice(tier.price_monthly)}</span>
                    {tier.price_monthly > 0 && (
                      <span className="text-muted-foreground">/Mo</span>
                    )}
                  </div>
                  {tier.price_monthly > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Billed Annually: {formatPrice(tier.price_monthly * 12 * 0.85)}
                    </p>
                  )}
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <ul className="space-y-3">
                    {tierFeatures[tierKey].map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    className="w-full"
                    variant={isCurrentTier ? "outline" : isPro ? "default" : "outline"}
                    disabled={isCurrentTier}
                    onClick={() => handleUpgrade(tierKey)}
                    data-testid={`subscribe-${tierKey}`}
                  >
                    {isCurrentTier ? "Paket Aktif" : tierKey === "free" ? "Absolutely Free!" : "Subscribe"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <div className="text-center text-sm text-muted-foreground max-w-2xl mx-auto">
          <p>
            Semua paket termasuk akses penuh ke fitur dasar Campus Link.
            Hubungi kami jika membutuhkan paket khusus untuk organisasi Anda.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionPage;
